// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { IRpcContext } from "@lib/messaging/rpc/IRpcContext";
import { RpcResult } from "./RpcResult";

/**
 * Provides methods for calling RPC methods.
 * The result of the RPC is type-safe, the arguments provided are not.
 * Use TypedRpcHub for argument type-safety.
 */
export type RpcHub = {
  invoke<T, E>(method: string, args: any[], timeout?: number): RpcResult<T, E>;
  notify(method: string, args: any[]): void;
}

type HubKeys<T> = { [P in keyof T]: T[P] extends (...args: any[]) => any ? P : never }[keyof T];
type HubCallbackParameter<T> = T extends (...args: infer TArgs) => any ? TArgs : any[];
type HubCallbackReturnType<T> = T extends (...args: any[]) => infer TResult ? TResult : any;

/**
 * Provides a type-safe way for calling RPC methods by using a hub type.
 * The hub type defines the methods available in the `RPCHub`.
 */
export type TypedRpcHub<TApi> = {
  invoke<TMethod extends HubKeys<TApi> & string, E = any>(method: TMethod, args: HubCallbackParameter<TApi[TMethod]>, timeout?: number): RpcResult<HubCallbackReturnType<TApi[TMethod]>, E>;
  notify<TMethod extends HubKeys<TApi> & string>(method: TMethod, args: HubCallbackParameter<TApi[TMethod]>): void;
};

export type LocalRpcHubImplementation<THub, TRpcContext extends IRpcContext> = { [P in HubKeys<THub> & string]: THub[P] extends (...args: infer TArgs) => infer TResult ? (context: TRpcContext, ...args: TArgs) => TResult : never };